import api from './api';
import { ApiResponse } from './auth';

export interface Project {
  id: number;
  data_collection_date: string;
  regional_center: string;
  planting_date: string;
  ecosystem_type?: 'mangrove' | 'tree' | 'coral' | 'seagrass';
  ecosystem_metadata?: Record<string, any> | null;
  assigned_team: string;
  local_collaborator?: string;
  location_url?: string;
  latitude?: number;
  longitude?: number;
  seedling_type: string;
  seedling_age_months: number;
  nursery_location: string;
  acclimatization_status: 'Iya' | 'Tidak';
  total_seedlings_planted: number;
  total_samples: number;
  avg_initial_leaves?: number;
  avg_initial_height_cm?: number;
  avg_initial_water_depth_cm?: number;
  water_ph?: number;
  water_salinity_ppt?: number;
  water_salinity_percentage?: number;
  water_temperature_celsius?: number;
  substrate_type: string;
  planting_method: string;
  area_hectares: number;
  observed_biota?: string;
  is_monitoring_finished: boolean;
  current_status?: string;
  partner_id?: number;
  created_by: number;
  created_at: string;
  updated_at: string;
  
  // Relationships
  partner?: {
    id: number;
    organization_name: string;
    contact_person: string;
  };
  project_images?: ProjectImage[];
  partners_count?: number;
  samples_count?: number;
  monitoring_sessions_count?: number;
  creator?: {
    id: number;
    name: string;
  };
}

export interface ProjectImage {
  id: number;
  project_id: number;
  image_path: string;
  image_url: string;
  original_name: string;
  mime_type: string;
  file_size: number;
  uploaded_by?: number;
  created_at: string;
  updated_at: string;
}

export interface CreateProjectData {
  data_collection_date: string;
  regional_center: string;
  planting_date: string;
  ecosystem_type?: 'mangrove' | 'tree' | 'coral' | 'seagrass';
  ecosystem_metadata?: Record<string, any>;
  assigned_team?: string;
  local_collaborator?: string;
  location_url?: string;
  latitude?: number;
  longitude?: number;
  seedling_type?: string;
  seedling_age_months?: number;
  nursery_location?: string;
  acclimatization_status?: 'Iya' | 'Tidak';
  total_seedlings_planted?: number;
  total_samples?: number;
  avg_initial_leaves?: number;
  avg_initial_height_cm?: number;
  avg_initial_water_depth_cm?: number;
  water_ph?: number;
  water_salinity_ppt?: number;
  water_salinity_percentage?: number;
  water_temperature_celsius?: number;
  substrate_type?: string;
  planting_method?: string;
  area_hectares?: number;
  observed_biota?: string;
  partner_id?: number;
}

export interface UpdateProjectData extends CreateProjectData {
  is_monitoring_finished?: boolean;
}

export interface ProjectsResponse {
  current_page: number;
  data: Project[];
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  links: Array<{
    url: string | null;
    label: string;
    page: number | null;
    active: boolean;
  }>;
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number;
  total: number;
}

export interface ProjectStats {
  total_projects: number;
  active_projects: number;
  completed_projects: number;
  total_seedlings_planted: number;
  total_area_hectares: string;
  total_samples?: number;
  total_monitoring_sessions?: number;
  total_users?: number;
  total_partners?: number;
  projects_by_partner: Record<string, number>;
  recent_projects: Project[];
}

class ProjectService {
  async getProjects(params?: {
    page?: number;
    search?: string;
    is_monitoring_finished?: boolean;
    partner_id?: number;
    start_date?: string;
    end_date?: string;
  }): Promise<ProjectsResponse> {
    const response = await api.get<ApiResponse<ProjectsResponse>>('/projects', { params });
    return response.data.data!;
  }

  async createProject(projectData: CreateProjectData): Promise<Project> {
    const response = await api.post<ApiResponse<Project>>('/projects', projectData);
    return response.data.data!;
  }

  async getProject(id: number): Promise<Project> {
    const userType = localStorage.getItem('userType') || 'internal';
    const endpoint = userType === 'partner' ? `/partner/projects/${id}` : `/projects/${id}`;
    const response = await api.get<ApiResponse<Project>>(endpoint);
    return response.data.data!;
  }

  async updateProject(id: number, projectData: UpdateProjectData): Promise<Project> {
    const response = await api.put<ApiResponse<Project>>(`/projects/${id}`, projectData);
    return response.data.data!;
  }

  async uploadProjectImage(id: number, file: File): Promise<ProjectImage> {
    const formData = new FormData();
    formData.append('image', file);

    const response = await api.post<ApiResponse<ProjectImage>>(`/projects/${id}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data.data!;
  }

  async deleteProjectImage(id: number, imageId: number): Promise<void> {
    await api.delete(`/projects/${id}/images/${imageId}`);
  }

  async deleteProject(id: number): Promise<void> {
    await api.delete(`/projects/${id}`);
  }

  async getProjectStats(): Promise<ProjectStats> {
    const response = await api.get<ApiResponse<ProjectStats>>('/projects-stats');
    return response.data.data!;
  }

  async getProjectPartners(id: number): Promise<any[]> {
    const response = await api.get<ApiResponse<any[]>>(`/projects/${id}/partners`);
    return response.data.data!;
  }

  async assignPartnerToProject(projectId: number, partnerId: number): Promise<void> {
    await api.post(`/projects/${projectId}/partners/${partnerId}`);
  }

  async removePartnerFromProject(projectId: number, partnerId: number): Promise<void> {
    await api.delete(`/projects/${projectId}/partners/${partnerId}`);
  }

  async getMyProjects(): Promise<Project[]> {
    const response = await api.get<ApiResponse<Project[]>>('/partner/my-projects');
    return response.data.data!;
  }
}

const projectService = new ProjectService();
export default projectService;
